import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { Role } from "@/utils/rbac";

// 🔹 Tipo del token decodificado
interface DecodedToken {
  usuario_id?: number;
  username?: string;
  roles?: Role[];                 // ⬅️ ahora solo roles (array)
  profesional_id?: number;
  exp: number;
}

// 🔹 Hook personalizado para manejar token de autenticación
export function useAuthToken() {
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));

  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [nombreUsuario, setNombreUsuario] = useState<string | null>(null);

  // ⬅️ YA NO USAMOS "rol"
  const [roles, setRoles] = useState<string[]>([]);

  const [profesionalId, setProfesionalId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 🔄 Escuchar cambios en localStorage o eventos manuales
  useEffect(() => {
    const checkToken = () => {
      const newToken = localStorage.getItem("token");
      setToken(newToken);
    };

    window.addEventListener("storage", checkToken);
    window.addEventListener("tokenChanged", checkToken);

    return () => {
      window.removeEventListener("storage", checkToken);
      window.removeEventListener("tokenChanged", checkToken);
    };
  }, []);

  // 🔑 Decodificar token
  useEffect(() => {
    if (!token) {
      setUsuarioId(null);
      setNombreUsuario(null);
      setRoles([]); // limpiar roles
      setProfesionalId(null);
      setError("No hay token válido");
      return;
    }

    try {
      const decoded = jwtDecode<DecodedToken>(token);

      // Expiración
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("token");
        setToken(null);
        setError("Token expirado");
        return;
      }

      // Guardar datos
      setUsuarioId(decoded.usuario_id ?? null);
      setNombreUsuario(decoded.username ?? null);

      // 🔥 Normalizar roles
      const rawRoles = decoded.roles ?? [];
      const cleanRoles = rawRoles.map(r =>
        r.replace(/^role_/i, "")
          .replace(/^role/i, "")
          .trim()
          .toLowerCase()
      );

      setRoles(cleanRoles);

      setProfesionalId(decoded.profesional_id ?? null);
      setError(null);

    } catch (err) {
      console.error("Error decodificando token:", err);
      setError("Token inválido");
      setToken(null);
    }
  }, [token]);

  // Logout
  const clearToken = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUsuarioId(null);
    setNombreUsuario(null);
    setRoles([]); // limpiar roles
    setProfesionalId(null);
    setError("Sesión cerrada.");
  };

  return {
    token,
    usuarioId,
    nombreUsuario,
    roles,          // ⬅️ ahora solo roles
    profesionalId,
    error,
    setToken,
    clearToken,
  };
}
